/**
 * Node Operation Handlers
 * Handles undo/redo for node CRUD operations (create, update, delete, move, batch)
 */

import type { Operation, NodeOperation, ProjectNode } from '@/shared/types/projectData';
import type { OperationHandler } from '../types';
import { getNodeId } from '@/features/projects/utils/projectUtils';
import { readReportBody } from '@/features/reports/utils/reportBody';

function normalizeParentId(value: string | null | undefined): string | null {
  return value ?? null;
}

type MoveHistoryState = {
  parentId?: string;
  order?: number;
  index?: number;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object';
}

function readMoveHistoryState(operation: NodeOperation): MoveHistoryState {
  if (operation.beforeMoveState) return operation.beforeMoveState;

  // History serialized before the dedicated field existed placed these keys in
  // beforeState. Read that persisted shape, but never write it again.
  return readLegacyMoveHistoryState(operation.beforeState);
}

function readLegacyMoveHistoryState(value: unknown): MoveHistoryState {
  if (!isRecord(value)) return {};
  const record = value;
  return {
    ...(typeof record.parentId === 'string' ? { parentId: record.parentId } : {}),
    ...(typeof record.order === 'number' ? { order: record.order } : {}),
    ...(typeof record.index === 'number' ? { index: record.index } : {}),
  };
}

function buildMoveUndoPayload(nodeOp: NodeOperation): NodeOperation | null {
  if (!nodeOp.nodeId) {
    return null;
  }

  const beforeState = readMoveHistoryState(nodeOp);
  const rawNodeId = getNodeId(nodeOp.nodeId);
  const previousParentId = normalizeParentId(beforeState.parentId);
  const currentParentId = normalizeParentId(nodeOp.targetParentId);
  const previousSiblingOrder = Array.isArray(nodeOp.beforeNodeIds) ? [...nodeOp.beforeNodeIds] : undefined;
  const currentSiblingOrder = Array.isArray(nodeOp.nodeIds) ? [...nodeOp.nodeIds] : undefined;
  const isCrossParentMove = previousParentId !== currentParentId;
  const inverseSourceNodeIds = currentSiblingOrder
    ? currentSiblingOrder.filter((id) => id !== rawNodeId)
    : undefined;

  return {
    type: 'move',
    nodeId: rawNodeId,
    targetParentId: previousParentId,
    targetIndex:
      typeof beforeState.index === 'number'
        ? beforeState.index
        : typeof beforeState.order === 'number'
          ? beforeState.order
          : 0,
    ...(previousSiblingOrder ? { nodeIds: previousSiblingOrder } : {}),
    ...(isCrossParentMove ? { sourceParentId: currentParentId } : {}),
    ...(isCrossParentMove && inverseSourceNodeIds ? { sourceNodeIds: inverseSourceNodeIds } : {}),
    timestamp: Date.now(),
    skipHistory: true,
  };
}

function buildMoveRedoPayload(nodeOp: NodeOperation): NodeOperation | null {
  if (!nodeOp.nodeId) {
    return null;
  }

  return {
    type: 'move',
    nodeId: getNodeId(nodeOp.nodeId),
    targetParentId: normalizeParentId(nodeOp.targetParentId),
    ...(typeof nodeOp.targetIndex === 'number' ? { targetIndex: nodeOp.targetIndex } : {}),
    ...(Array.isArray(nodeOp.nodeIds) ? { nodeIds: [...nodeOp.nodeIds] } : {}),
    ...(nodeOp.sourceParentId !== undefined ? { sourceParentId: normalizeParentId(nodeOp.sourceParentId) } : {}),
    ...(Array.isArray(nodeOp.sourceNodeIds) ? { sourceNodeIds: [...nodeOp.sourceNodeIds] } : {}),
    timestamp: Date.now(),
    skipHistory: true,
  };
}

export function buildUpdateUndoData(nodeOp: Pick<NodeOperation, 'data' | 'beforeState'>): Partial<ProjectNode> {
  const changedData = nodeOp.data || {};
  const beforeState = nodeOp.beforeState || {};
  const undoData: Record<string, unknown> = {};
  const beforeStateRecord = beforeState as Record<string, unknown>;

  Object.keys(changedData).forEach((key) => {
    if (
      Object.prototype.hasOwnProperty.call(beforeStateRecord, key) &&
      beforeStateRecord[key] !== undefined
    ) {
      undoData[key] = beforeStateRecord[key];
      return;
    }

    undoData[key] = key === 'developmentRoles' ? [] : null;
  });

  if (
    undoData.workstream === 'development' &&
    !Object.prototype.hasOwnProperty.call(undoData, 'developmentRoles')
  ) {
    undoData.developmentRoles = Array.isArray(beforeState.developmentRoles)
      ? [...beforeState.developmentRoles]
      : [];
  }

  return undoData as Partial<ProjectNode>;
}

export function buildBatchUpdateUndoPayload(
  nodeOp: Pick<NodeOperation, 'updates' | 'beforeStates' | 'metadata' | 'description'>
): NodeOperation | null {
  if (!nodeOp.updates || !nodeOp.beforeStates) {
    return null;
  }

  const beforeStateByNodeId = new Map(
    nodeOp.beforeStates.map((entry) => [entry.nodeId, entry.beforeState])
  );

  const updates = nodeOp.updates
    .map((update) => {
      const beforeState = beforeStateByNodeId.get(update.nodeId);
      if (!update.nodeId || !beforeState) {
        return null;
      }

      return {
        nodeId: update.nodeId,
        data: buildUpdateUndoData({
          data: update.data,
          beforeState,
        }),
      };
    })
    .filter((update): update is { nodeId: string; data: Partial<ProjectNode> } => update !== null);

  if (updates.length === 0) {
    return null;
  }

  return {
    type: 'batch-update',
    updates,
    timestamp: Date.now(),
    description: `Undo: ${nodeOp.description || 'Batch update'}`,
    metadata: nodeOp.metadata,
    skipHistory: true,
  };
}

function getRestoreDepth(node: ProjectNode, nodeMap: Map<string, ProjectNode>): number {
  let depth = 0;
  let parentId = node.parentId;
  const visited = new Set<string>();

  while (parentId && nodeMap.has(parentId) && !visited.has(parentId)) {
    visited.add(parentId);
    depth += 1;
    parentId = nodeMap.get(parentId)?.parentId;
  }

  return depth;
}

function sortNodesForRestore(nodes: ProjectNode[]): ProjectNode[] {
  const nodeMap = new Map(nodes.map((node) => [node.id, node]));

  return [...nodes].sort((a, b) => {
    const depthDiff = getRestoreDepth(a, nodeMap) - getRestoreDepth(b, nodeMap);
    if (depthDiff !== 0) {
      return depthDiff;
    }

    const parentDiff = String(a.parentId ?? '').localeCompare(String(b.parentId ?? ''));
    if (parentDiff !== 0) {
      return parentDiff;
    }

    return (a.order ?? 0) - (b.order ?? 0);
  });
}

function buildRestoreCreateOperation(
  node: ProjectNode,
  targetIndex?: number
): NodeOperation {
  return {
    type: 'create',
    nodeId: node.id,
    data: { ...node },
    parentId: node.parentId ?? undefined,
    ...(node.projectId ? { metadata: { projectId: node.projectId } } : {}),
    targetIndex: targetIndex ?? node.order,
    timestamp: Date.now(),
    skipHistory: true,
  };
}

/**
 * Register all node operation handlers
 */
export function registerNodeOperationHandlers(
  handlers: Map<string, OperationHandler>,
  applyOp: (op: NodeOperation) => Promise<void>
): void {
  // Create
  handlers.set('create', {
    undo: async (op: Operation) => {
      const nodeOp = op as NodeOperation;
      await applyOp({
        type: 'delete',
        nodeId: nodeOp.nodeId || '',
        metadata: nodeOp.metadata,
        timestamp: Date.now(),
        skipHistory: true
      });
    },
    redo: async (op: Operation) => {
      const nodeOp = op as NodeOperation;
      await applyOp({
        type: 'create',
        nodeId: nodeOp.nodeId || nodeOp.data?.id,
        data: nodeOp.data,
        parentId: nodeOp.parentId,
        targetIndex: nodeOp.targetIndex,
        metadata: nodeOp.metadata,
        timestamp: Date.now(),
        skipHistory: true
      });
    }
  });

  // Update
  handlers.set('update', {
    undo: async (op: Operation) => {
      const nodeOp = op as NodeOperation;
      if (!nodeOp.nodeId || (!nodeOp.expectedFields && !nodeOp.beforeState)) return;

      await applyOp({
        type: 'update',
        nodeId: nodeOp.nodeId,
        // Conditional history's baseline is authoritative; local beforeState may be stale.
        data: nodeOp.expectedFields ?? buildUpdateUndoData(nodeOp),
        ...(nodeOp.expectedFields !== undefined ? { expectedFields: nodeOp.data } : {}),
        metadata: nodeOp.metadata?.persistence === 'report-http'
          ? { ...nodeOp.metadata, expectedReportBody: readReportBody(nodeOp.data || {}) }
          : nodeOp.metadata,
        timestamp: Date.now(),
        skipHistory: true
      });
    },
    redo: async (op: Operation) => {
      const nodeOp = op as NodeOperation;
      if (!nodeOp.nodeId || !nodeOp.data) return;

      await applyOp({
        type: 'update',
        nodeId: nodeOp.nodeId,
        data: nodeOp.data,
        ...(nodeOp.expectedFields !== undefined ? { expectedFields: nodeOp.expectedFields } : {}),
        metadata: nodeOp.metadata?.persistence === 'report-http'
          ? { ...nodeOp.metadata, expectedReportBody: readReportBody(nodeOp.beforeState || {}) }
          : nodeOp.metadata,
        timestamp: Date.now(),
        skipHistory: true
      });
    }
  });

  // Delete
  handlers.set('delete', {
    undo: async (op: Operation) => {
      const nodeOp = op as NodeOperation;
      const deletedTree = (nodeOp as NodeOperation & { deletedTree?: { root: ProjectNode; descendants: ProjectNode[] } }).deletedTree;

      if (deletedTree?.root && deletedTree?.descendants) {
        // Restore root
        await applyOp(buildRestoreCreateOperation(
          deletedTree.root,
          nodeOp.originalIndex ?? deletedTree.root.order
        ));

        // Restore descendants
        const sortedDescendants = sortNodesForRestore(deletedTree.descendants);

        for (const descendant of sortedDescendants) {
          await applyOp(buildRestoreCreateOperation(descendant));
        }
      } else if (nodeOp.deletedNode) {
        // Fallback
        await applyOp(buildRestoreCreateOperation(
          nodeOp.deletedNode,
          nodeOp.originalIndex ?? nodeOp.deletedNode.order
        ));
      }
    },
    redo: async (op: Operation) => {
      const nodeOp = op as NodeOperation;
      if (!nodeOp.nodeId) return;

      await applyOp({
        type: nodeOp.type === 'delete-tree' ? 'delete-tree' : 'delete',
        nodeId: nodeOp.nodeId,
        metadata: nodeOp.metadata,
        timestamp: Date.now(),
        skipHistory: true
      });
    }
  });
  handlers.set('delete-tree', {
    undo: async op => {
      const nodeOp = op as NodeOperation;
      if (!nodeOp.sourceOperationId) throw new Error('Deletion receipt is unavailable; the subtree was not restored.');
      await applyOp({type:'restore-tree',nodeId:nodeOp.nodeId,sourceOperationId:nodeOp.sourceOperationId,
        metadata:nodeOp.metadata,skipHistory:true});
    },
    redo: async op => {
      const nodeOp = op as NodeOperation;
      const request:NodeOperation = {type:'delete-tree',nodeId:nodeOp.nodeId,metadata:nodeOp.metadata,skipHistory:true};
      await applyOp(request);
      if (request.sourceOperationId) nodeOp.sourceOperationId = request.sourceOperationId;
    },
  });

  // Move
  handlers.set('move', {
    undo: async (op: Operation) => {
      const nodeOp = op as NodeOperation;
      const undoPayload = buildMoveUndoPayload(nodeOp);
      if (!undoPayload) return;
      await applyOp(undoPayload);
    },
    redo: async (op: Operation) => {
      const nodeOp = op as NodeOperation;
      const redoPayload = buildMoveRedoPayload(nodeOp);
      if (!redoPayload) return;
      await applyOp(redoPayload);
    }
  });

  // Batch update
  handlers.set('batch-update', {
    undo: async (op: Operation) => {
      const nodeOp = op as NodeOperation;
      const undoPayload = buildBatchUpdateUndoPayload(nodeOp);
      if (!undoPayload) return;
      await applyOp(undoPayload);
    },
    redo: async (op: Operation) => {
      const nodeOp = op as NodeOperation;
      if (!nodeOp.updates) return;

      await applyOp({
        type: 'batch-update',
        updates: nodeOp.updates,
        metadata: nodeOp.metadata,
        timestamp: Date.now(),
        skipHistory: true
      });
    }
  });

  // Batch delete
  handlers.set('batch-delete', {
    undo: async (op: Operation) => {
      const nodeOp = op as NodeOperation;
      if (!nodeOp.deletedNodes || !Array.isArray(nodeOp.deletedNodes)) return;

      const deletedNodeMap = new Map<string, { node: ProjectNode; parentId?: string; originalIndex: number }>();
      nodeOp.deletedNodes.forEach((deletedNode) => {
        deletedNodeMap.set(deletedNode.node.id, deletedNode);
      });

      const sortedNodes = sortNodesForRestore(
        Array.from(deletedNodeMap.values()).map((deletedNode) => deletedNode.node)
      );

      for (const node of sortedNodes) {
        const deletedNode = deletedNodeMap.get(node.id);
        if (!deletedNode) continue;

        await applyOp({
          ...buildRestoreCreateOperation(
            node,
            deletedNode.originalIndex ?? node.order
          ),
          parentId: deletedNode.parentId ?? node.parentId,
        });
      }
    },
    redo: async (op: Operation) => {
      const nodeOp = op as NodeOperation;
      if (!nodeOp.nodeIds) return;

      await applyOp({
        type: 'batch-delete',
        nodeIds: nodeOp.nodeIds,
        timestamp: Date.now(),
        skipHistory: true
      });
    }
  });

  // Progress update
  handlers.set('progress-update', {
    undo: async (op: Operation) => {
      const nodeOp = op as NodeOperation;
      if (typeof nodeOp.beforeProgress !== 'number' || !nodeOp.nodeId) return;

      await applyOp({
        type: 'progress-update',
        nodeId: nodeOp.nodeId,
        progress: nodeOp.beforeProgress,
        timestamp: Date.now(),
        skipHistory: true
      });
    },
    redo: async (op: Operation) => {
      const nodeOp = op as NodeOperation;
      if (!nodeOp.nodeId || typeof nodeOp.progress !== 'number') return;

      await applyOp({
        type: 'progress-update',
        nodeId: nodeOp.nodeId,
        progress: nodeOp.progress,
        timestamp: Date.now(),
        skipHistory: true
      });
    }
  });

  // Group move
  handlers.set('group-move', {
    undo: async (operation: Operation) => {
      const op = operation as NodeOperation;

      // Preferred: inverse group-move
      if (op.nodeId && typeof op.daysDelta === 'number') {
        await applyOp({
          type: 'group-move',
          nodeId: op.nodeId,
          daysDelta: -op.daysDelta,
          description: `Undo: ${op.description}`,
          timestamp: Date.now(),
          skipHistory: true
        });
        return;
      }

      // Fallback: per-node restore
      if (op.beforeStates && Array.isArray(op.beforeStates)) {
        for (const beforeStateEntry of op.beforeStates) {
          if (beforeStateEntry.nodeId && beforeStateEntry.beforeState) {
            await applyOp({
              type: 'update',
              nodeId: beforeStateEntry.nodeId,
              data: beforeStateEntry.beforeState,
              timestamp: Date.now(),
              skipHistory: true
            });
          }
        }
      }
    },
    redo: async (operation: Operation) => {
      const op = operation as NodeOperation;
      if (!op.nodeId || op.daysDelta === undefined) return;

      await applyOp({
        type: 'group-move',
        nodeId: op.nodeId,
        daysDelta: op.daysDelta,
        description: `Redo: ${op.description}`,
        timestamp: Date.now(),
        skipHistory: true
      });
    }
  });
}
